import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { IDateRangeAsDates } from '@core/models/IDates';
import { environment } from '@environments/environment';
import { Paginated, Params, ResDataItem } from '@core/models/IApi';
import { Filter } from '@features/social-analysis/models/IFilters';
import { BaseState, DATA_STATUS } from '@features/social-analysis/social-shared/store/state.store';
import dayjs from 'dayjs';
import { ToastService } from '@shared/services/toast.service';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/internal/operators/finalize';
import { map } from 'rxjs/internal/operators/map';

export abstract class HttpBuilder<
    TState extends BaseState<any> = BaseState<any>,
    TRes extends ResDataItem = ResDataItem,
> {
    protected http = inject(HttpClient);
    protected toastr = inject(ToastService);

    /* REQ BUILDING */

    protected buildUrl(endpoint: string) {
        return environment.api_base + endpoint;
    }

    protected getDates({ startDate, endDate }: IDateRangeAsDates) {
        if (!startDate || !endDate) return;
        return { since: this.dateToStr(startDate), until: this.dateToStr(endDate) };
    }

    protected dateToStr(date: Date) {
        return dayjs(date).format('YYYY-MM-DD');
    }

    protected formatFilters(filts: Filter[]) {
        return filts.reduce((acc, curr) => {
            const pref = curr.dim.prefix ? curr.dim.prefix + '.' : '';
            const key = pref + curr.dim.value;
            const op = curr.operator ? curr.operator + ':' : 'in:';
            const value = curr.value.join();
            return { ...acc, [key]: op + value };
        }, {} as Params);
    }

    /* REQ MAKING */

    protected makeGetReq(url: string, params: Params, state: TState) {
        this.subscribeToHttp$(this.buildGetHttp$(url, params), state);
    }

    protected buildGetHttp$<T>(url: string, params: Params) {
        return this.http.get<T>(url, { params: params });
    }

    protected subscribeToHttp$(http$: Observable<Paginated<TRes[]>>, state: TState) {
        return http$
            .pipe(
                map(res => this.formatRes(res)),
                finalize(() => this.setIsLoading(state, false))
            )
            .subscribe({
                next: data => this.handleSuccess(state, data),
                error: err => this.handleError(err?.error?.message),
            });
    }

    protected setIsLoading(state: TState, isLoading: boolean) {
        state.isLoading.set(isLoading);
    }

    protected handleSuccess(state: TState, data: any) {
        state.data.set(data);
        state.dataStatus.set(DATA_STATUS.OK);
    }

    protected handleError(err?: string) {
        this.toastr.error(err || 'Please try again later');
    }

    protected formatRes(res: Paginated<TRes[]>) {
        return res.data.map(obj => this.flattenOneLevel(obj));
    }

    protected flattenOneLevel(obj: ResDataItem): Record<string, any> {
        const keys = Object.keys(obj);
        let result = {};

        if (keys.length === 0) return result;
        keys.forEach(key =>
            typeof obj[key] === 'object'
                ? (result = { ...result, ...this.addEntityToKey(key, obj[key] as ResDataItem) })
                : (result = { ...result, [key]: obj[key] })
        );

        return result;
    }

    protected addEntityToKey(prefix: string, obj: ResDataItem): object {
        const formatted = Object.keys(obj).reduce(
            (acc, key) => ({
                ...acc,
                [`${prefix}.${key}`]: obj[key],
            }),
            {}
        );
        return formatted;
    }
}
